/**
 * PDF Text Extraction Service
 * Extracts text content from PDF pages using pdfjs-dist
 */

import * as pdfjsLib from 'pdfjs-dist';

export interface PageTextContent {
  pageIndex: number;
  text: string;
  items: Array<{
    str: string;
    transform: number[];
    width: number;
    height: number;
  }>;
}

// Cache for extracted page text to avoid re-extraction
const pageTextCache = new Map<string, PageTextContent>();

/**
 * Generate a cache key for a page
 */
function getCacheKey(docFingerprint: string, pageIndex: number): string {
  return `${docFingerprint}-${pageIndex}`;
}

/**
 * Extract text content from a single PDF page
 *
 * @param pdfDocument - The PDF document proxy from pdfjs-dist
 * @param pageIndex - 0-based page index
 * @returns PageTextContent with extracted text and items
 */
export async function extractPageText(
  pdfDocument: pdfjsLib.PDFDocumentProxy,
  pageIndex: number
): Promise<PageTextContent> {
  // Check cache first
  const fingerprint = pdfDocument.fingerprints?.[0] || 'unknown';
  const cacheKey = getCacheKey(fingerprint, pageIndex);

  const cached = pageTextCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    // PDF.js uses 1-based page numbers
    const page = await pdfDocument.getPage(pageIndex + 1);
    const textContent = await page.getTextContent();

    const items: PageTextContent['items'] = [];
    const textParts: string[] = [];

    for (const item of textContent.items) {
      // TextItem has 'str' property
      if ('str' in item && typeof item.str === 'string') {
        textParts.push(item.str);
        items.push({
          str: item.str,
          transform: item.transform as number[],
          width: item.width as number,
          height: item.height as number,
        });
      }
    }

    const result: PageTextContent = {
      pageIndex,
      text: textParts.join(' '),
      items,
    };

    // Cache the result
    pageTextCache.set(cacheKey, result);

    return result;
  } catch (error) {
    console.error(`Failed to extract text from page ${pageIndex}:`, error);
    return {
      pageIndex,
      text: '',
      items: [],
    };
  }
}

/**
 * Extract text from multiple PDF pages
 *
 * @param pdfDocument - The PDF document proxy from pdfjs-dist
 * @param pageIndices - Array of 0-based page indices (if empty, extracts all pages)
 * @returns Array of PageTextContent
 */
export async function extractDocumentText(
  pdfDocument: pdfjsLib.PDFDocumentProxy,
  pageIndices?: number[]
): Promise<PageTextContent[]> {
  const indicesToExtract =
    pageIndices && pageIndices.length > 0
      ? pageIndices
      : Array.from({ length: pdfDocument.numPages }, (_, i) => i);

  const results = await Promise.all(
    indicesToExtract.map((pageIndex) => extractPageText(pdfDocument, pageIndex))
  );

  return results;
}

/**
 * Clear the text extraction cache
 * Call this when switching documents or to free memory
 */
export function clearTextCache(): void {
  pageTextCache.clear();
}

/**
 * Clear cache for a specific document
 */
export function clearDocumentCache(docFingerprint: string): void {
  for (const key of pageTextCache.keys()) {
    if (key.startsWith(docFingerprint)) {
      pageTextCache.delete(key);
    }
  }
}
